/* eslint-disable indent */
/**
 * Utility functions for SkillDistribution components
 * Shared between general and single-player modes
 */

/**
 * Parse age string (e.g., "17.0" or "17.56") to [years, days] array
 */
export const parseAgeStr = str => {
  const v = Number(str)
  if (!isFinite(v)) return [17, 0]
  const years = Math.floor(v)
  const frac = Math.max(0, v - years)
  const days = Math.round(frac * 112)
  return [years, days]
}

/**
 * Map frontend skill format to backend skill format
 */
export const mapStartToBackendSkills = skills => ({
  keeperSkill: Number(skills.GK) || 0,
  defenderSkill: Number(skills.D) || 0,
  playmakerSkill: Number(skills.PM) || 0,
  wingerSkill: Number(skills.W) || 0,
  passingSkill: Number(skills.PS) || 0,
  scorerSkill: Number(skills.SC) || 0,
  setPiecesSkill: Number(skills.SP) || 0,
})

/**
 * Map position to group for filtering (GK, Defender, Inner, Forward)
 */
export const getPositionGroup = pos => {
  // Remove player ID suffix if exists (e.g., "CD_123456789" -> "CD")
  const basePos = pos.split('_')[0]

  if (basePos === 'GK' || basePos === 'GKD') return 'GK'
  if (
    [
      'CD',
      'RCD',
      'LCD',
      'CDO',
      'RCDTW',
      'LCDTW',
      'RWBD',
      'LWBD',
      'RWBN',
      'LWBN',
      'RWBO',
      'LWBO',
      'RWBTM',
      'LWBTM',
    ].includes(basePos)
  )
    return 'Defender'
  if (
    [
      'RWO',
      'LWO',
      'RWTM',
      'LWTM',
      'RWN',
      'LWN',
      'RWD',
      'LWD',
      'IMN',
      'IMD',
      'IMO',
      'RIMTW',
      'LIMTW',
    ].includes(basePos)
  )
    return 'Inner'
  if (['FW', 'RFTW', 'LFTW', 'DF'].includes(basePos)) return 'Forward'
  return 'All'
}

/**
 * Get default starting skills for a position
 */
export const getDefaultStartForPosition = pos => {
  if (pos === 'GKD') {
    return { GK: 1, D: 8, PM: 3, W: 3, PS: 3, SC: 3, SP: 3 }
  }

  // Extract base position from unique ID if needed
  const basePos = pos.split('_')[0]

  const base = {
    GK: 3,
    D: 3,
    PM: 3,
    W: 3,
    PS: 3,
    SC: 3,
    SP: 3,
  }
  if (basePos === 'GK') {
    return { ...base, GK: 7, D: 5, SP: 6 }
  }
  // Central defenders (contains 'CD')
  if (basePos.includes('CD')) {
    return { ...base, D: 7, PM: 6 }
  }
  // Wing backs (contains 'WB')
  if (basePos.includes('WB')) {
    return { ...base, D: 7, PM: 5, W: 6 }
  }
  // Inner mids (contains IM)
  if (basePos.includes('IM')) {
    return { ...base, D: 5, PM: 7, PS: 7 }
  }
  // Forwards must be checked before wingers because FTW includes "W".
  if (
    basePos.startsWith('FW') ||
    basePos.includes('FTW') ||
    basePos.includes('TDF')
  ) {
    return { ...base, PM: 5, W: 5, PS: 5, SC: 7 }
  }
  // Wingers (has W but not WB, and often starts with RW/LW)
  if (basePos.includes('W') && !basePos.includes('WB')) {
    return { ...base, D: 5, PM: 6, W: 7, PS: 4 }
  }
  return base
}

/**
 * Format age array to string (e.g., [17, 56] -> "17.50")
 */
export const formatAge = arr => {
  if (!Array.isArray(arr) || arr.length < 2) return '-'
  const val = Number(arr[0]) + Number(arr[1] || 0) / 112
  if (!isFinite(val)) return '-'
  return val.toFixed(2)
}

/**
 * Get required skills for a specific position based on requiredSkills map
 */
export const getRequiredSkillsForPosition = (position, requiredSkills) => {
  const result = {}
  const basePos = position.split('_')[0]

  for (const [key, value] of Object.entries(requiredSkills)) {
    const separatorIndex = key.indexOf('_')
    const skill = key.slice(0, separatorIndex)
    const target = key.slice(separatorIndex + 1)
    // All positions
    if (target === 'All') {
      result[skill] = value
      continue
    }
    // Group-based targets
    if (['Defender', 'Inner', 'Forward', 'GK'].includes(target)) {
      if (getPositionGroup(basePos) === target) {
        result[skill] = value
      }
      continue
    }
    // Exact position target
    if (basePos === target || position === target) {
      result[skill] = value
    }
  }
  return result
}

/**
 * Get forced skills for a specific position based on forcedSkills map
 */
export const getForcedSkillsForPosition = (position, forcedSkills) => {
  const result = {}
  const basePos = position.split('_')[0]

  for (const [key, value] of Object.entries(forcedSkills)) {
    const separatorIndex = key.indexOf('_')
    const skill = key.slice(0, separatorIndex)
    const target = key.slice(separatorIndex + 1)
    if (target === 'All') {
      result[skill] = value
      continue
    }
    if (['Defender', 'Inner', 'Forward', 'GK'].includes(target)) {
      if (getPositionGroup(basePos) === target) {
        result[skill] = value
      }
      continue
    }
    if (basePos === target || position === target) {
      result[skill] = value
    }
  }
  return result
}

/**
 * Get max skills for a specific position based on maxSkills map
 */
export const getMaxSkillsForPosition = (position, maxSkills) => {
  const result = {}
  const basePos = position.split('_')[0]

  for (const [key, value] of Object.entries(maxSkills)) {
    const separatorIndex = key.indexOf('_')
    const skill = key.slice(0, separatorIndex)
    const target = key.slice(separatorIndex + 1)
    if (target === 'All') {
      result[skill] = value
      continue
    }
    if (['Defender', 'Inner', 'Forward', 'GK'].includes(target)) {
      if (getPositionGroup(basePos) === target) {
        result[skill] = value
      }
      continue
    }
    if (basePos === target || position === target) {
      result[skill] = value
    }
  }
  return result
}

/**
 * Build multipliers payload from state
 */
export const buildMultipliersPayload = multipliers => {
  const mp = {}
  Object.entries(multipliers || {}).forEach(([k, v]) => {
    const num = Number(String(v).replace(',', '.'))
    if (isFinite(num) && num !== 1 && num !== 0) mp[k] = num
  })
  return mp
}

/**
 * Build addable position options (collapse R/L variants)
 */
export const buildAddableOptions = positions => {
  const labelToValue = new Map()
  positions.forEach(p => {
    const isRL = p.startsWith('R') || p.startsWith('L')
    const label = isRL ? p.slice(1) : p
    if (labelToValue.has(label)) return
    if (isRL) {
      const r = 'R' + label
      const l = 'L' + label
      if (positions.includes(r)) labelToValue.set(label, r)
      else if (positions.includes(l)) labelToValue.set(label, l)
      else labelToValue.set(label, p)
    } else {
      labelToValue.set(label, p)
    }
  })
  const options = []
  labelToValue.forEach((value, label) => {
    options.push({ value, label })
  })
  // Keep GK first, then alphabetical by label
  options.sort((a, b) =>
    a.label === 'GK'
      ? -1
      : b.label === 'GK'
      ? 1
      : a.label.localeCompare(b.label)
  )
  return options
}

/**
 * Extract display label for a position
 * @param {string} pos - Position ID (may include player ID suffix)
 * @param {object} positionLabels - Map of position IDs to labels
 * @returns {string} Display label
 */
export const getPositionDisplayLabel = (pos, positionLabels = {}) => {
  // If custom label exists, use it
  if (positionLabels[pos]) {
    return positionLabels[pos]
  }

  if (pos === 'GKD') return 'GKD'

  // Extract base position (remove player ID suffix)
  const basePos = pos.split('_')[0]

  // Remove R/L prefix for display
  return basePos.startsWith('R') || basePos.startsWith('L')
    ? basePos.slice(1)
    : basePos
}

/**
 * Check if a position ID contains a valid player ID (9-digit number)
 */
export const hasPlayerId = pos => {
  return pos.includes('_') && /\d{9}/.test(pos)
}

/**
 * Extract player ID from position string
 */
export const extractPlayerId = pos => {
  const parts = pos.split('_')
  if (parts.length >= 2) {
    const match = parts[1].match(/\d{9}/)
    if (match) return match[0]
  }
  return null
}

/**
 * Skill order constant for consistent ordering
 */
export const SKILL_ORDER = [
  'keeperSkill',
  'defenderSkill',
  'playmakerSkill',
  'wingerSkill',
  'passingSkill',
  'scorerSkill',
  'setPiecesSkill',
]

/**
 * Default training parameters
 */
export const DEFAULT_TRAINING_PARAMS = {
  coach: '5',
  assistants: '10',
  intensity: '100',
  stamina: '10',
  minutes: '90',
  efficiency: '1',
}

/**
 * Default skill caps
 */
export const DEFAULT_CAPS = {
  keeperSkill: '22',
  defenderSkill: '20',
  playmakerSkill: '20',
  wingerSkill: '20',
  passingSkill: '20',
  scorerSkill: '20',
  setPiecesSkill: '20',
}

/**
 * Konvertoi vanhat caps (scoringSkill -> scorerSkill)
 */
export const migrateCaps = caps => {
  if (!caps) return DEFAULT_CAPS
  const migrated = { ...caps }
  // Jos vanhan muotoinen scoringSkill on olemassa, kopioi se scorerSkill:iin
  if (
    migrated.scoringSkill !== undefined &&
    migrated.scorerSkill === undefined
  ) {
    migrated.scorerSkill = migrated.scoringSkill
    delete migrated.scoringSkill
  }
  return migrated
}

/**
 * Default active positions
 */
export const DEFAULT_ACTIVE_POSITIONS = [
  'GK',
  'CD',
  'RWBN',
  'RWTM',
  'RWN',
  'IMN',
  'IMD',
  'IMO',
  'FW',
  'RFTW',
]

/**
 * Format decimal to percent display string
 */
export const formatPercentDisplay = v => {
  if (v === '' || v === undefined || v === null) return ''
  const num =
    typeof v === 'number' ? v : parseFloat(String(v).replace(',', '.'))
  if (!isFinite(num)) return String(v)
  const p = num * 100
  const rounded = Number.isInteger(p) ? String(p) : p.toFixed(2)
  return `${rounded}%`
}

/**
 * Parse percent input (like "87" or "87.5%") to decimal
 */
export const parsePercentToDecimal = raw => {
  if (raw === '' || raw === undefined || raw === null) return ''
  const cleaned = String(raw).replace('%', '').replace(',', '.').trim()
  const num = parseFloat(cleaned)
  if (!isFinite(num)) return ''
  return num / 100
}
